import type { Campaign } from '../shared/rules.js';
import type { MenuAction } from './MenuView.js';
import { attachTextTooltip } from './CardView.js';
import { showToast } from './Share.js';

export type CampaignState = 'available' | 'completed' | 'locked';

/** A campaign is locked iff any of its `requiredCompletions` stage IDs is
 *  missing from the player's beaten set. Completed iff every one of its own
 *  stages is in the beaten set. Otherwise available. */
export function getCampaignState(campaign: Campaign, beatenStageIds: string[]): CampaignState {
  const beaten = new Set(beatenStageIds);
  for (const reqId of campaign.requiredCompletions ?? []) {
    if (!beaten.has(reqId)) return 'locked';
  }
  if (campaign.stages.every(s => beaten.has(s.id))) return 'completed';
  return 'available';
}

export function createCampaignListView(
  container: HTMLElement,
  campaigns: Campaign[],
  beatenStageIds: string[],
  onAction: (action: MenuAction) => void,
): void {
  container.innerHTML = '';
  const el = document.createElement('div');
  el.className = 'menu-screen campaign-list-screen';

  const visible = campaigns.filter(c => !c.hidden);

  el.innerHTML = `
    <div class="campaign-header">
      <button class="menu-btn campaign-back" id="back-btn">← Back</button>
      <h1 class="menu-title">Campaigns</h1>
    </div>
    <div class="campaign-grid"></div>
  `;

  el.querySelector('#back-btn')!.addEventListener('click', () => onAction({ type: 'back-to-menu' }));

  const grid = el.querySelector('.campaign-grid')!;
  for (const campaign of visible) {
    const state = getCampaignState(campaign, beatenStageIds);
    const tile = document.createElement('div');
    tile.className = `campaign-tile ${state}`;
    tile.innerHTML = `
      ${state === 'locked' ? '<div class="lock-badge">🔒</div>' : ''}
      ${state === 'completed' ? '<div class="completion-badge">✓</div>' : ''}
      <h2 class="campaign-tile-name">${escapeHtml(campaign.name)}</h2>
      <p class="campaign-tile-description">${escapeHtml(campaign.description)}</p>
      <p class="campaign-tile-progress">${stageProgressLabel(campaign, beatenStageIds)}</p>
    `;
    if (state === 'locked') {
      // Locked tiles still react to clicks: hover the lock icon for a
      // tooltip and tap anywhere on the tile to flash a toast with the
      // same reason. Without this the player has no signal for *why*
      // the campaign is gated — `cursor: not-allowed` alone is too
      // ambiguous (it could mean "data missing", "premium", etc.).
      const reason = lockReasonText(campaign, campaigns);
      const lockBadge = tile.querySelector('.lock-badge') as HTMLElement | null;
      if (lockBadge) attachTextTooltip(lockBadge, () => reason);
      tile.addEventListener('click', () => showToast(reason));
    } else {
      tile.classList.add('clickable');
      tile.addEventListener('click', () => onAction({ type: 'view-campaign', campaignId: campaign.id }));
    }
    grid.appendChild(tile);
  }

  container.appendChild(el);
}

/** Build the "Complete X to unlock" line for a locked campaign tile.
 *  Resolves each prerequisite stage id to "<stage> (<campaign>)" so the
 *  player knows which stage to chase across all campaigns, not just one
 *  ID. Multiple prerequisites are joined with commas and a final "and". */
function lockReasonText(campaign: Campaign, allCampaigns: Campaign[]): string {
  const reqs = campaign.requiredCompletions ?? [];
  if (reqs.length === 0) return 'Locked';
  const parts: string[] = [];
  for (const reqId of reqs) {
    let resolved = reqId;
    for (const c of allCampaigns) {
      const stage = c.stages.find(s => s.id === reqId);
      if (stage) {
        resolved = `${stage.name} (${c.name})`;
        break;
      }
    }
    parts.push(resolved);
  }
  if (parts.length === 1) return `Complete ${parts[0]} to unlock`;
  const head = parts.slice(0, -1).join(', ');
  const tail = parts[parts.length - 1];
  return `Complete ${head} and ${tail} to unlock`;
}

function stageProgressLabel(campaign: Campaign, beatenStageIds: string[]): string {
  const beaten = new Set(beatenStageIds);
  const done = campaign.stages.filter(s => beaten.has(s.id)).length;
  return `${done} / ${campaign.stages.length} stages`;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
